import { useEffect, useReducer, useRef } from 'react';
import { callApi } from 'src/utils/apiCall';

const savedText = (value) => (typeof value === 'string' && value.trim() ? value : null);
export const callReviewScope = (practiceId, call, recording) =>
  JSON.stringify([
    practiceId,
    call?.channel,
    call?.id,
    call?._refs?.phoneId,
    call?._refs?.businessId,
    call?._refs?.callId,
    recording?.id,
    recording?.recordingUrl,
  ]);

// Retain in-flight/uncertain work across closing and reopening the same loaded
// call. A refreshed call object is a new saved-state snapshot. Weak keys allow
// removed rows (and their results) to be collected instead of retaining patient data.
const callJobs = new WeakMap();
// A list refresh must not bypass a request still executing for the same call.
// Entries exist only until settlement; saved results belong to the loaded row.
const inFlight = new Map();
const jobsFor = (call) => {
  if (!call || typeof call !== 'object') return new Map();
  if (!callJobs.has(call)) callJobs.set(call, new Map());
  return callJobs.get(call);
};

export default function useCallReviewData({ practiceId, call, recording, onTranscriptReady }) {
  const scope = callReviewScope(practiceId, call, recording);
  const callScope = callReviewScope(practiceId, call, null);
  const current = useRef({ scope, call, epoch: 0 });
  if (current.current.scope !== scope || current.current.call !== call) {
    current.current = { scope, call, epoch: current.current.epoch + 1 };
  }
  const mounted = useRef(false);
  const [, render] = useReducer((value) => value + 1, 0);
  const jobs = jobsFor(call);
  const transcriptKey = `${scope}:transcript`;
  const reviewKey = `${scope}:review`;
  const transcriptRequestKey = `${callScope}:transcript-request`;
  const reviewRequestKey = `${callScope}:review-request`;
  const completedReviewKey = `${callScope}:completed-review`;
  [
    [transcriptKey, transcriptRequestKey],
    [reviewRequestKey, reviewRequestKey],
  ].forEach(([key, requestKey]) => {
    if (!jobs.has(key) && inFlight.has(requestKey)) jobs.set(key, inFlight.get(requestKey));
  });
  const transcriptJob = jobs.get(transcriptKey);
  const reviewJob = jobs.get(reviewKey);
  useEffect(() => {
    mounted.current = true;
    const token = current.current;
    const refresh = () => {
      if (mounted.current && current.current === token) render();
    };
    // Reopening attaches to an existing request; it never starts another POST.
    [jobs.get(transcriptKey), jobs.get(reviewKey), jobs.get(reviewRequestKey)].forEach((job) =>
      job?.promise?.then(refresh)
    );
    return () => {
      mounted.current = false;
    };
  }, [scope, jobs, transcriptKey, reviewKey, reviewRequestKey]);
  const refs = call?._refs || {};
  const whatsapp = call?.channel === 'whatsapp';
  const valid = Boolean(practiceId && refs.phoneId && refs.callId && (!whatsapp || refs.businessId));
  const url = whatsapp
    ? `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${refs.businessId}/whatsapp-phones/${refs.phoneId}/calls/${refs.callId}`
    : `/api/v1/practices/${practiceId}/practice-twilio-phones/${refs.phoneId}/calls/${refs.callId}`;
  const multiple = Math.max(call?.recordingCount || 0, call?.recordings?.length || 0) > 1;
  const transcript =
    transcriptJob?.value || savedText(recording?.transcript) || (!multiple ? savedText(call?.transcript) : null);
  const review = reviewJob?.value || jobs.get(completedReviewKey)?.value || call?.aiReview || null;
  const canGenerateTranscript = valid && !whatsapp && !multiple && Boolean(recording?.recordingUrl);
  const canGenerateReview =
    valid &&
    Boolean(transcript || savedText(call?.transcript) || call?.recordings?.some((item) => savedText(item.transcript)));
  const generate = async (kind) => {
    const key = kind === 'transcript' ? transcriptKey : reviewKey;
    const requestKey = kind === 'review' ? reviewRequestKey : transcriptRequestKey;
    const existing = inFlight.get(requestKey) || (kind === 'review' ? jobs.get(reviewRequestKey) : jobs.get(key));
    if (
      existing?.status === 'loading' ||
      existing?.status === 'uncertain' ||
      !(kind === 'transcript' ? canGenerateTranscript : canGenerateReview)
    )
      return;
    const token = current.current;
    const active = () => mounted.current && current.current === token;
    const job = { status: 'loading', value: null };
    jobs.set(key, job);
    if (kind === 'review') jobs.set(reviewRequestKey, job);
    inFlight.set(requestKey, job);
    render();
    job.promise = (async () => {
      try {
        const response = await callApi(
          `${url}/${kind === 'transcript' ? 'transcribe' : 'ai-review'}/`,
          'POST',
          null,
          false,
          { timeout: 60000 }
        );
        const value =
          kind === 'transcript'
            ? savedText(
                response.data?.transcript || response.data?.transcription || response.data?.text || response.data
              )
            : response.data?.ai_review;
        if (!active()) {
          job.status = 'uncertain';
          return;
        }
        if (response.ok && value) {
          job.value = value;
          job.status = 'idle';
          if (kind === 'review' && active()) jobs.set(completedReviewKey, { value, status: 'idle' });
          if (kind === 'transcript' && active()) onTranscriptReady?.(call.id, value);
        } else {
          // Without a readback/status endpoint, transport/server errors may have
          // completed remotely. Refresh saved state before repeating the POST.
          job.status = [400, 401, 403, 404, 405, 422].includes(response.status) ? 'error' : 'uncertain';
        }
      } catch (_) {
        job.status = 'uncertain';
      } finally {
        if (inFlight.get(requestKey) === job) inFlight.delete(requestKey);
        if (active()) render();
      }
    })();
    await job.promise;
  };
  return {
    transcript,
    review,
    transcriptState: transcriptJob?.status || 'idle',
    reviewState: reviewJob?.status || jobs.get(reviewRequestKey)?.status || 'idle',
    canGenerateTranscript,
    canGenerateReview,
    generateTranscript: () => generate('transcript'),
    generateReview: () => generate('review'),
  };
}
